import { useCallback, useEffect, useRef, useState } from "react";
import type { Channel as StreamChannelType } from "stream-chat";
import { useChatContext } from "stream-chat-react";

const ARCHIVED_PAGE_SIZE = 30;

// Manages the collapsible "Archived conversations" section: paginated
// fetching plus unarchive (which must also refresh the main ChannelList).
export const useArchivedChannels = (userId: string, onRefreshList: () => void) => {
  const { client } = useChatContext();
  const [expanded, setExpanded] = useState(false);
  const [channels, setChannels] = useState<StreamChannelType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);

  // Fetch archived channels (first page)
  const fetchArchived = useCallback(async () => {
    if (!client || !userId) return;
    setLoading(true);
    offsetRef.current = 0;
    try {
      const response = await client.queryChannels(
        {
          type: "messaging",
          members: { $in: [userId] },
          archived: true,
        },
        { last_message_at: -1 },
        { watch: true, state: true, limit: ARCHIVED_PAGE_SIZE, offset: 0 },
      );
      setChannels(response);
      setHasMore(response.length === ARCHIVED_PAGE_SIZE);
      offsetRef.current = response.length;
    } catch (err) {
      console.error("Failed to fetch archived channels:", err);
    } finally {
      setLoading(false);
    }
  }, [client, userId]);

  const loadMore = useCallback(async () => {
    if (!client || !userId || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await client.queryChannels(
        {
          type: "messaging",
          members: { $in: [userId] },
          archived: true,
        },
        { last_message_at: -1 },
        { watch: true, state: true, limit: ARCHIVED_PAGE_SIZE, offset: offsetRef.current },
      );
      setChannels((prev) => [...prev, ...response]);
      setHasMore(response.length === ARCHIVED_PAGE_SIZE);
      offsetRef.current += response.length;
    } catch (err) {
      console.error("Failed to load more archived channels:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [client, userId, loadingMore]);

  const unarchive = useCallback(
    async (ch: StreamChannelType) => {
      if (!client.userID) return;
      try {
        await ch.updateMemberPartial(
          { set: { archived: false } },
          { userId: client.userID },
        );
        setChannels((prev) => prev.filter((c) => c.cid !== ch.cid));
        onRefreshList();
      } catch (err) {
        console.error("Failed to unarchive:", err);
      }
    },
    [client, onRefreshList],
  );

  // Load archived channels when section is expanded
  useEffect(() => {
    if (expanded) {
      fetchArchived();
    }
  }, [expanded, fetchArchived]);

  return { expanded, setExpanded, channels, loading, loadingMore, hasMore, loadMore, unarchive };
};
